declare global {
  interface Window {
    Swiper: any;
  }
}

export interface EkSliderArrowRules {
  left: "md-only" | "always";
  right: "md-only" | "always";
}

export interface EkSliderOptions {
  sectionEl: HTMLElement;
  sectionId: string;
  swiperEl: HTMLElement;
  hasProductCards?: boolean;
  arrowRules?: EkSliderArrowRules;
  swiperOverrides?: Record<string, unknown>;
  resolveLockTranslate?: (swiper: any) => number;
}

export interface EkSliderInstance {
  sectionEl: HTMLElement;
  sectionId: string;
  swiper: any;
  update: () => void;
  destroy: () => void;
}

const ekSliderInstances = new Map<string, EkSliderInstance>();

// Product-card sliders: mobile needs slidesOffsetBefore because Swiper ignores
// container padding; desktop aligns with the headline via md:-mx-5 / lg:-mx-10.
export const productSliderSwiperOverrides: Record<string, unknown> = {
  slidesOffsetBefore: 20,
  slidesOffsetAfter: 20,
  breakpoints: {
    768: { spaceBetween: 16, slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
    1024: { spaceBetween: 16, slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
  },
};

// WebKit can drop the end of a touch gesture (e.g. when videos start/stop
// nearby), leaving Swiper's permanent document-level non-passive touchmove
// handler in a state that preventDefault()s all native scrolling. Once no
// fingers remain on the screen, reset the touch state of every registered
// instance. Deferred a macrotask so healthy gestures are processed first.
const stuckTouchResetRegistry = new Set<any>();
let touchSequence = 0;

export function registerSwiperForStuckTouchReset(swiper: any): void {
  installStuckTouchSafetyNet();
  stuckTouchResetRegistry.add(swiper);
}

export function unregisterSwiperForStuckTouchReset(swiper: any): void {
  stuckTouchResetRegistry.delete(swiper);
}

function resetStuckTouchState(event: TouchEvent): void {
  if (event.touches.length > 0) return;

  const sequenceAtRelease = touchSequence;
  setTimeout(() => {
    if (sequenceAtRelease !== touchSequence) return;

    for (const swiper of stuckTouchResetRegistry) {
      const data = swiper?.touchEventsData;
      if (!data) continue;
      data.isTouched = false;
      data.isMoved = false;
      data.startMoving = false;
      data.touchId = null;
      data.preventTouchMoveFromPointerMove = false;
    }
  }, 0);
}

function installStuckTouchSafetyNet(): void {
  if (document.documentElement.dataset.ekSliderTouchResetInit === "true") return;
  document.documentElement.dataset.ekSliderTouchResetInit = "true";
  document.addEventListener(
    "touchstart",
    () => {
      touchSequence += 1;
    },
    { passive: true },
  );
  document.addEventListener("touchend", resetStuckTouchState, { passive: true });
  document.addEventListener("touchcancel", resetStuckTouchState, { passive: true });
}

function installSectionUnloadListener(): void {
  if (document.documentElement.dataset.ekSliderUnloadInit === "true") return;
  document.documentElement.dataset.ekSliderUnloadInit = "true";
  document.addEventListener("shopify:section:unload", (event) => {
    const target = (event.target as ParentNode) || document;
    destroyEkSlidersIn(target);
  });
}

function unlockSwatchSwipers(sectionEl: HTMLElement): void {
  const swatchEls = sectionEl.querySelectorAll<HTMLElement>('.product-card[data-swatches-lock-initial="true"] .swatch-swiper');
  for (const swatchEl of Array.from(swatchEls)) {
    const swatchSwiper = (swatchEl as any).swiper;
    if (!swatchSwiper) continue;
    try {
      swatchSwiper.allowTouchMove = true;
      swatchSwiper.update();
    } catch {
      // card swiper may be mid-initialization; the next lock/resize pass retries
    }
  }
}

export function getEkSlider(sectionId: string): EkSliderInstance | undefined {
  return ekSliderInstances.get(sectionId);
}

export function destroyEkSlidersIn(root: ParentNode): void {
  for (const [sectionId, instance] of Array.from(ekSliderInstances.entries())) {
    const inRoot = root === document || (root instanceof Node && root.contains(instance.sectionEl));
    if (!inRoot) continue;
    instance.destroy();
    ekSliderInstances.delete(sectionId);
  }
}

export function createEkSlider(options: EkSliderOptions): EkSliderInstance | null {
  const { sectionEl, sectionId, swiperEl } = options;
  if (!sectionEl || !sectionId || !swiperEl) return null;
  if (sectionEl.dataset.ekSliderInit === "true") return ekSliderInstances.get(sectionId) ?? null;

  if (typeof window.Swiper !== "function") {
    setTimeout(() => createEkSlider(options), 50);
    return null;
  }

  sectionEl.dataset.ekSliderInit = "true";
  installSectionUnloadListener();

  const hasProductCards = options.hasProductCards !== false;
  const arrowRules: EkSliderArrowRules = options.arrowRules ?? { left: "md-only", right: "md-only" };
  const overrides = options.swiperOverrides ?? {};
  const resolveLockTranslate = options.resolveLockTranslate;

  const applyLockTranslate = (): void => {
    const translate = resolveLockTranslate?.(swiper) ?? swiper.minTranslate();
    swiper.setTranslate(translate);
  };

  const swiper = new window.Swiper(swiperEl, {
    slidesPerView: "auto",
    spaceBetween: 12,
    freeMode: {
      enabled: true,
      momentum: true,
      momentumBounce: false,
      sticky: false,
    },
    watchOverflow: true,
    threshold: 8,
    grabCursor: true,
    mousewheel: {
      forceToAxis: true,
      releaseOnEdges: true,
      sensitivity: 1,
    },
    touchEventsTarget: "wrapper",
    touchReleaseOnEdges: true,
    breakpoints: {
      768: { spaceBetween: 16 },
    },
    ...overrides,
  });

  registerSwiperForStuckTouchReset(swiper);

  const leftBtn = sectionEl.querySelector<HTMLButtonElement>(`#recs-arrow-left-${sectionId}`);
  const rightBtn = sectionEl.querySelector<HTMLButtonElement>(`#recs-arrow-right-${sectionId}`);

  const setArrowVisible = (btn: HTMLButtonElement | null, visible: boolean, rule: "md-only" | "always", isMdUp: boolean): void => {
    if (!btn) return;
    const show = visible && (rule === "always" || isMdUp);
    btn.style.display = show ? "flex" : "none";
  };

  const updateArrows = (): void => {
    const isMdUp = window.matchMedia?.("(min-width: 768px)").matches ?? window.innerWidth >= 768;
    const canScroll = !swiper.isLocked;
    setArrowVisible(leftBtn, canScroll && !swiper.isBeginning, arrowRules.left, isMdUp);
    setArrowVisible(rightBtn, canScroll && !swiper.isEnd, arrowRules.right, isMdUp);
  };

  // Arrows scroll by 80% of the visible width (legacy sliderCore UX),
  // not per-slide stepping — hence custom handlers instead of Swiper navigation.
  const scrollByStep = (direction: 1 | -1): void => {
    const step = Math.max(swiper.width * 0.8, 200);
    const raw = swiper.translate - direction * step;
    const target = Math.max(swiper.maxTranslate(), Math.min(swiper.minTranslate(), raw));
    swiper.translateTo(target, 300);
  };

  leftBtn?.addEventListener("click", () => scrollByStep(-1));
  rightBtn?.addEventListener("click", () => scrollByStep(1));

  const onLock = (): void => {
    applyLockTranslate();
    updateArrows();
    if (hasProductCards) unlockSwatchSwipers(sectionEl);
  };

  swiper.on("lock", onLock);
  swiper.on("unlock", updateArrows);
  swiper.on("setTranslate", updateArrows);
  swiper.on("transitionEnd", updateArrows);
  swiper.on("touchEnd", updateArrows);
  swiper.on("resize", () => {
    updateArrows();
    if (hasProductCards && swiper.isLocked) unlockSwatchSwipers(sectionEl);
  });

  if (swiper.isLocked) onLock();
  updateArrows();

  if (hasProductCards) {
    // Card swipers lazy-init via IntersectionObserver and may appear after
    // the lock event already fired — retry once they had a chance to mount.
    setTimeout(() => {
      if (swiper.destroyed) return;
      if (swiper.isLocked) unlockSwatchSwipers(sectionEl);
    }, 300);
  }

  const instance: EkSliderInstance = {
    sectionEl,
    sectionId,
    swiper,
    update: () => {
      swiper.update();
      updateArrows();
    },
    destroy: () => {
      unregisterSwiperForStuckTouchReset(swiper);
      try {
        swiper.destroy(true, true);
      } catch {}
      delete sectionEl.dataset.ekSliderInit;
      ekSliderInstances.delete(sectionId);
    },
  };

  ekSliderInstances.set(sectionId, instance);
  return instance;
}
